"use client";

import { useCallback, useMemo } from "react";
import {
  assertRecoveredAddressMatches,
  assertSignatureRoundTrip,
  splitSignature,
  type HyperliquidSignatureComponents,
  type TypedDataDefinition,
  HYPERLIQUID_ZERO_ADDRESS,
  HYPERLIQUID_DEFAULT_CHAIN_ID,
} from "@cede/utils";
import { HYPERLIQUID_TRANSFER_DOMAIN } from "@/constants/hyperliquid";
import { useAuthenticatedWalletConnection } from "@/hooks/core/useAuthenticatedWalletConnection";
import { useSignTypedDataWithConnection } from "@/hooks/core/useSignTypedDataWithConnection";
import type { ApproveAgentRequest, UsdTransferRequest } from "@/types/hyperliquid";

interface SignedHyperliquidAction<T extends ApproveAgentRequest | UsdTransferRequest> {
  signature: HyperliquidSignatureComponents;
  action: T;
}

/**
 * Hook for signing Hyperliquid typed data with wagmi's `useSignTypedData`
 */
export function useHyperliquidSign() {
  const { ensureAuthenticatedWalletConnection, authenticatedWalletAddress } = useAuthenticatedWalletConnection();
  const { signTypedDataAsync, isPending: isSigning } = useSignTypedDataWithConnection();

  const signTransfer = useCallback(
    async (request: Omit<UsdTransferRequest, "type">): Promise<SignedHyperliquidAction<UsdTransferRequest>> => {
      const { address } = await ensureAuthenticatedWalletConnection();

      const parsedChainId = Number.parseInt(request.signatureChainId, 16);
      const signatureChainId = Number.isNaN(parsedChainId) ? HYPERLIQUID_DEFAULT_CHAIN_ID : parsedChainId;

      const domain = {
        ...HYPERLIQUID_TRANSFER_DOMAIN,
        chainId: signatureChainId,
        verifyingContract: HYPERLIQUID_ZERO_ADDRESS,
      };

      const types = {
        "HyperliquidTransaction:UsdClassTransfer": [
          { name: "hyperliquidChain", type: "string" },
          { name: "amount", type: "string" },
          { name: "toPerp", type: "bool" },
          { name: "nonce", type: "uint64" },
        ],
      } satisfies TypedDataDefinition;

      const message = {
        hyperliquidChain: request.hyperliquidChain,
        amount: request.amount,
        toPerp: request.toPerp,
        nonce: BigInt(request.nonce),
      };

      const signature = await signTypedDataAsync({
        domain,
        types,
        primaryType: "HyperliquidTransaction:UsdClassTransfer",
        message,
      });

      // Non mandatory: ensure signature matches payload
      await assertRecoveredAddressMatches({
        signature,
        domain,
        types,
        primaryType: "HyperliquidTransaction:UsdClassTransfer",
        message,
        expectedAddress: address,
      });

      const components = splitSignature(signature);
      assertSignatureRoundTrip(signature, components);

      return {
        signature: components,
        action: {
          type: "usdClassTransfer",
          ...request,
        },
      };
    },
    [ensureAuthenticatedWalletConnection, signTypedDataAsync],
  );

  return useMemo(
    () => ({
      signTransfer,
      isSigning,
      address: authenticatedWalletAddress,
    }),
    [authenticatedWalletAddress, isSigning, signTransfer],
  );
}
